// TasksEmptyStateCard — shared empty-state card for the checklist TASKS tab.
//
// Renders two visual variants:
//   'all-complete' — green success card: every checklist step is done.
//   'no-tasks'     — neutral info card: no checklist items exist at all.
//
// CTA priority (button selection):
//   onCreateReminder provided  → "Create a custom reminder" (primary, opens ScheduleTaskReminder)
//   onCreateReminder null + onClose provided → "Close" (green, preserves existing Attended behaviour)
//   both null                  → no button rendered
//
// This fallback keeps parity: Attended all-done cards previously showed a green
// Close button.  Any consumer that doesn't wire onCreateReminder still gets Close.
//
// Used by:
//   FollowUpV2.jsx               — passes onCreateReminder, no onClose
//   AttendedGuidedChecklistV2    — passes both (reminder CTA first, Close fallback)
//   AttendedGuidedChecklist (v1) — passes both (reminder CTA first, Close fallback)
import PropTypes from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';

// ---------------------------------------------------------------------------

const VARIANTS = {
    'all-complete': {
        testId: 'all-tasks-complete-card',
        ctaTestId: 'all-tasks-complete-cta',
        bgColor: 'rgba(0,171,85,0.06)',
        border: '1px solid rgba(0,171,85,0.18)',
        iconBg: 'rgba(0,171,85,0.12)',
        icon: 'solar:check-circle-bold',
        iconColor: '#00AB55',
        heading: 'All follow-ups complete',
        subtext: "Every step in this lead’s checklist is done.",
    },
    'no-tasks': {
        testId: 'no-tasks-card',
        ctaTestId: 'no-tasks-cta',
        bgColor: 'rgba(145,158,171,0.06)',
        border: '1px solid rgba(145,158,171,0.18)',
        iconBg: 'rgba(145,158,171,0.12)',
        icon: 'solar:clipboard-list-bold',
        iconColor: '#637381',
        heading: 'No tasks for this lead',
        subtext: 'There are no checklist items yet.',
    },
};

// ---------------------------------------------------------------------------

export default function TasksEmptyStateCard({ variant, onCreateReminder, onClose, onContactLead }) {
    const v = VARIANTS[variant];
    if (!v) return null;

    return (
        <Box
            data-testid={v.testId}
            sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                py: 4, px: 3,
                bgcolor: v.bgColor,
                borderRadius: '16px',
                border: v.border,
                textAlign: 'center',
            }}
        >
            <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: v.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <Iconify icon={v.icon} width={28} height={28} sx={{ color: v.iconColor }} />
            </Box>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                {v.heading}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5, maxWidth: 280 }}>
                {v.subtext}
            </Typography>

            {/* CTA row: primary = reminder form or Close fallback; secondary = Contact lead. */}
            {(onCreateReminder || onContactLead) && (
                <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center">
                    {onCreateReminder && (
                        <Button
                            data-testid={v.ctaTestId}
                            variant="contained"
                            disableElevation
                            onClick={onCreateReminder}
                            startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
                            sx={{
                                ...CRM_PRIMARY_BUTTON_SX,
                                fontSize: '14px', fontWeight: 600, letterSpacing: '-0.01em',
                                textTransform: 'none', borderRadius: '6px', px: 3, py: 0.875,
                            }}
                        >
                            Create a custom reminder
                        </Button>
                    )}
                    {/* Opens the Follow-up channel picker (Call / WA / Email / Note) for
                        the active treatment when no checklist tasks exist yet. */}
                    {onContactLead && (
                        <Button
                            data-testid="no-tasks-contact-lead-btn"
                            variant="outlined"
                            onClick={onContactLead}
                            startIcon={<Iconify icon="mdi:phone-outline" width={18} height={18} />}
                            sx={{
                                fontSize: '14px', fontWeight: 600, letterSpacing: '-0.01em',
                                textTransform: 'none', borderRadius: '6px', px: 3, py: 0.875,
                                borderColor: 'rgba(0,0,0,0.23)',
                                color: '#1d1d1f',
                                '&:hover': { borderColor: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                            }}
                        >
                            Contact lead
                        </Button>
                    )}
                </Stack>
            )}
            {!onCreateReminder && !onContactLead && onClose && (
                <Button
                    data-testid="all-done-close-btn"
                    variant="contained"
                    disableElevation
                    onClick={onClose}
                    sx={{
                        bgcolor: '#34c759',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        textTransform: 'none',
                        borderRadius: '6px',
                        px: 3,
                        py: 0.875,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#2db14e', boxShadow: 'none' },
                    }}
                >
                    Close
                </Button>
            )}
        </Box>
    );
}

TasksEmptyStateCard.propTypes = {
    /** Which empty-state variant to render. */
    variant: PropTypes.oneOf(['all-complete', 'no-tasks']).isRequired,
    /** When provided, renders "Create a custom reminder" as the primary CTA. */
    onCreateReminder: PropTypes.func,
    /** Fallback when both onCreateReminder and onContactLead are null — renders legacy "Close" button. */
    onClose: PropTypes.func,
    /** When provided, renders a "Contact lead" secondary button that opens the follow-up channel picker. */
    onContactLead: PropTypes.func,
};

TasksEmptyStateCard.defaultProps = {
    onCreateReminder: null,
    onClose: null,
    onContactLead: null,
};
